import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CoreLoadingScreenService } from '@core/services/loading-screen.service';
import { EndpointService } from '../service/endpoint.service';

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private readonly exceptAPIList = ['/api/User?userId'];

  private readonly whitelistUrls = [
    'https://benai.app-npe.mybenefits.ro/api/v2/response',
    'https://benai.mybenefits.ro/api/v2/response',
  ];

  // Requests for which a message is shown below the spinner while they are in flight.
  private readonly uploadUrls = [
    EndpointService.changeProfilePicture,
    EndpointService.addFixedBenefitExpenseReport,
  ];

  // Order placement endpoints that only involve a file upload when the benefit's
  // checkout policy requires documents to be submitted along with the order.
  private readonly conditionalUploadUrls = [
    EndpointService.postSimpleOrder,
    EndpointService.postDirectOrder,
  ];
  private trackedRequests = new Set<string>();
  private activeUploadRequests = new Set<string>();
  private alreadyHidden = false;

  constructor(
    private _loading: CoreLoadingScreenService,
    private _translate: TranslateService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isWhitelisted = this.whitelistUrls.some(url => request.url.includes(url));
    const isExcluded = this.exceptAPIList.some(url => request.url.includes(url));
    const shouldTrack = !isWhitelisted && !isExcluded;

    const requestId = `${request.method}-${request.urlWithParams}`;

    if (shouldTrack) {
      const isUpload = this.isUploadRequest(request);

      if (!this._loading.isVisible) {
        this.alreadyHidden = false;
        const message = isUpload ? this._translate.instant('UPLOAD_FILE.UPLOAD_IN_PROGRESS') : undefined;
        this._loading.show(message);
      } else if (isUpload) {
        // Another request is already tracked (e.g. a checkout-policy refresh that
        // synchronously kicks off this upload request before its own finalize runs).
        // Make sure the message still appears even though this isn't the "first" request.
        this._loading.updateMessage(this._translate.instant('UPLOAD_FILE.UPLOAD_IN_PROGRESS'));
      }

      if (isUpload) {
        this.activeUploadRequests.add(requestId);
      }
      this.trackedRequests.add(requestId);
    } else {
      this._loading.displayInnerLoader();
    }

    return next.handle(request).pipe(
      finalize(() => {
        if (shouldTrack) {
          this.trackedRequests.delete(requestId);
          this.activeUploadRequests.delete(requestId);

          if (this.trackedRequests.size > 0 && this.activeUploadRequests.size === 0) {
            // Other requests are still in flight, but none of them are uploads anymore.
            this._loading.updateMessage(undefined);
          }

          this.checkAndHideLoader();
        }
      })
    );
  }

  private isUploadRequest(request: HttpRequest<any>): boolean {
    if (this.uploadUrls.some(url => request.url.includes(url))) {
      return true;
    }

    if (this.conditionalUploadUrls.some(url => request.url.includes(url))) {
      const requiredDocuments = request.body?.submittedRequiredDocuments;
      return Array.isArray(requiredDocuments) && requiredDocuments.length > 0;
    }

    return false;
  }

  private checkAndHideLoader() {
    if (this.trackedRequests.size === 0 && !this.alreadyHidden) {
      this.alreadyHidden = true;
      this._loading.hide();
      this._loading.hideInnerLoader();
    }
  }
}
